// Notification Sound Utility using Web Audio API
// Generates an alarm/siren sound for new orders

class NotificationSound {
  constructor() {
    this.audioContext = null;
    this.isEnabled = localStorage.getItem('orderSoundEnabled') !== 'false'; // Default enabled
    this.loopIntervalId = null;
    this.isLooping = false;
  }

  // Initialize audio context (required for browser autoplay policies)
  initAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }

  // Play alarm sound - Creates a siren-like sound for 10 seconds
  playAlarmSound() {
    if (!this.isEnabled) return;

    try {
      const ctx = this.initAudioContext();
      const now = ctx.currentTime;
      const duration = 10; // 10 seconds total duration
      const cycleLength = 0.6; // Each siren cycle is 0.6 seconds

      // Create oscillators for a siren effect
      const oscillator1 = ctx.createOscillator();
      const oscillator2 = ctx.createOscillator();
      const gainNode = ctx.createGain();

      // Connect nodes
      oscillator1.connect(gainNode);
      oscillator2.connect(gainNode);
      gainNode.connect(ctx.destination);

      // Configure oscillator types
      oscillator1.type = 'sine';
      oscillator2.type = 'sine';

      // Create repeating siren pattern for 10 seconds
      // Main oscillator frequency pattern (repeating)
      let currentTime = now;
      oscillator1.frequency.setValueAtTime(800, currentTime);
      oscillator2.frequency.setValueAtTime(600, currentTime);

      // Create multiple cycles of siren sound
      const numCycles = Math.floor(duration / cycleLength);
      for (let i = 0; i < numCycles; i++) {
        const cycleStart = now + (i * cycleLength);
        const cycleHalf = cycleStart + (cycleLength / 2);
        const cycleEnd = cycleStart + cycleLength;

        // First oscillator (high frequency sweep)
        oscillator1.frequency.exponentialRampToValueAtTime(1200, cycleHalf);
        oscillator1.frequency.exponentialRampToValueAtTime(800, cycleEnd);

        // Second oscillator (lower frequency harmony)
        oscillator2.frequency.exponentialRampToValueAtTime(900, cycleHalf);
        oscillator2.frequency.exponentialRampToValueAtTime(600, cycleEnd);
      }

      // Volume envelope (fade in/out)
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.35, now + 0.1); // Quick fade in
      gainNode.gain.linearRampToValueAtTime(0.35, now + duration - 0.5); // Sustain
      gainNode.gain.linearRampToValueAtTime(0, now + duration); // Fade out

      // Start and stop
      oscillator1.start(now);
      oscillator2.start(now);
      oscillator1.stop(now + duration);
      oscillator2.stop(now + duration);

      console.log('🔔 New order notification sound played for 10 seconds!');
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  }

  // Start continuous looping alarm for pending orders
  startContinuousAlarm() {
    if (!this.isEnabled || this.isLooping) return;

    console.log('🔔 Starting continuous alarm for pending orders...');
    this.isLooping = true;

    // Play immediately
    this.playAlarmSound();

    // Continue playing every 12 seconds (10s sound + 2s gap)
    this.loopIntervalId = setInterval(() => {
      if (this.isLooping) {
        this.playAlarmSound();
      }
    }, 12000);
  }

  // Stop continuous alarm
  stopContinuousAlarm() {
    if (this.loopIntervalId) {
      console.log('🔕 Stopping continuous alarm');
      clearInterval(this.loopIntervalId);
      this.loopIntervalId = null;
    }
    this.isLooping = false;
  }

  // Alias for backward compatibility
  stopSound() {
    this.stopContinuousAlarm();
  }

  // Play a shorter "ding" sound for other notifications
  playDingSound() {
    if (!this.isEnabled) return;

    try {
      const ctx = this.initAudioContext();
      const now = ctx.currentTime;

      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, now);
      oscillator.frequency.exponentialRampToValueAtTime(1000, now + 0.1);

      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.2, now + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, now + 0.3);

      oscillator.start(now);
      oscillator.stop(now + 0.3);
    } catch (error) {
      console.error('Error playing ding sound:', error);
    }
  }

  // Toggle sound on/off
  setEnabled(enabled) {
    this.isEnabled = enabled;
    localStorage.setItem('orderSoundEnabled', enabled.toString());

    // Stop continuous alarm if disabling
    if (!enabled) {
      this.stopContinuousAlarm();
    }
  }

  // Get current enabled state
  getEnabled() {
    return this.isEnabled;
  }

  // Get looping state
  isPlayingContinuously() {
    return this.isLooping;
  }

  // Test the sound (useful for settings)
  testSound() {
    this.playAlarmSound();
  }
}

// Create singleton instance
const notificationSound = new NotificationSound();

export default notificationSound;
